import { useNavigate } from 'react-router-dom';
import { ChangeEvent, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  login,
  authStatusSelector,
  authErrorSelector,
  clearError,
  currentUserSelector,
} from '../redux/slices/userSlice';

/**
 * Custom hook to handle login input and submission.
 *
 * @returns username - The current value of the username input.
 * @returns password - The current value of the password input.
 * @returns handleInputChange - Function to handle changes in the input field.
 * @returns handleSubmit - Function to handle login submission.
 * @returns error - The error message from the login attempt, if any.
 * @returns status - The current status of the login attempt.
 */
const useLogin = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Access authentication status and error from Redux
  const status = useAppSelector(authStatusSelector);
  const error = useAppSelector(authErrorSelector);
  const currentUser = useAppSelector(currentUserSelector);

  useEffect(() => {
    if (status === 'idle' && currentUser) {
      // Only navigate if authentication is confirmed (authStatus is not 'loading')
      navigate('/home', { replace: true });
    }
  }, [status, currentUser, navigate]);

  /**
   * Function to handle the input change event.
   *
   * @param e - the event object.
   * @param field - the field to update.
   */
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>, field: 'username' | 'password') => {
    const { value } = e.target;
    if (field === 'username') setUsername(value);
    else if (field === 'password') setPassword(value);
  };

  /**
   * Function to handle the form submission event.
   *
   * @param event - the form event object.
   */
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    await dispatch(clearError()); // Clear any existing error

    // Dispatch the login action with username and password
    const action = await dispatch(login({ username, password }));

    // If the login was successful, navigate to the home page
    if (login.fulfilled.match(action)) {
      navigate('/home');
    }
  };

  /**
   * Function to handle navigation to the create account page.
   */
  const handleCreateAccountNav = () => {
    dispatch(clearError()); // Clear any existing error
    navigate('/create-account');
  };

  return {
    username,
    password,
    handleInputChange,
    handleSubmit,
    handleCreateAccountNav,
    status,
    error,
    currentUser,
  };
};

export default useLogin;
